import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "components/Card";
import InputField from "components/InputField";
import TextField from "components/TextField";
import Button from "components/Button";
import Slider from "components/Slider";
import SelectField from "components/SelectField";
import Checkbox from "components/Checkbox";
import SelectAllButton from "components/SelectAllButton";
import {
  getAppraisalCycleAPI,
  updateAppraisalCycleAPI,
  getAllTemplatesAPI,
  fetchAllEmployeesAPI,
} from "api/apiHelper";
import { toast } from "react-toastify";
import { Template } from "types/template-builder-types";
import { AppraisalCycle } from "types/appraisal-cycle-types";

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  full_name?: string;
}

const EditCycle = () => {
  const { cycleId } = useParams<{ cycleId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    form_opens: "",
    deadline: "",
    is_active: true,
    self_review_template_id: "",
    manager_review_template_id: "",
    peer_review_template_id: "",
    client_review_template_id: "",
    enable_self_review: false,
    enable_manager_review: false,
    enable_peer_review: false,
    enable_client_review: false,
    self_review_weightage: 0,
    manager_review_weightage: 0,
    peer_review_weightage: 0,
    client_review_weightage: 0,
  });

  const [selectedParticipantIds, setSelectedParticipantIds] = useState<number[]>([]);

  useEffect(() => {
    if (cycleId) {
      fetchCycleData();
      fetchTemplates();
      fetchEmployees();
    }
  }, [cycleId]);

  const fetchCycleData = async () => {
    try {
      setIsLoading(true);
      const response = await getAppraisalCycleAPI(Number(cycleId));
      const cycle: AppraisalCycle = response.data;

      const formatDateTime = (dateString: string) => {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };

      setFormData({
        name: cycle.name,
        description: cycle.description || "",
        form_opens: formatDateTime(cycle.form_opens),
        deadline: formatDateTime(cycle.deadline),
        is_active: cycle.is_active,
        self_review_template_id: cycle.self_review_template?.toString() || "",
        manager_review_template_id: cycle.manager_review_template?.toString() || "",
        peer_review_template_id: cycle.peer_review_template?.toString() || "",
        client_review_template_id: cycle.client_review_template?.toString() || "",
        enable_self_review: cycle.enable_self_review,
        enable_manager_review: cycle.enable_manager_review,
        enable_peer_review: cycle.enable_peer_review,
        enable_client_review: cycle.enable_client_review,
        self_review_weightage: parseFloat(cycle.self_review_weightage),
        manager_review_weightage: parseFloat(cycle.manager_review_weightage),
        peer_review_weightage: parseFloat(cycle.peer_review_weightage),
        client_review_weightage: parseFloat(cycle.client_review_weightage),
      });

      setSelectedParticipantIds(cycle.participants.map((p) => p.id));
    } catch (error: any) {
      toast.error("Failed to load cycle data");
      navigate("/admin-appraisals?tab=1");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await getAllTemplatesAPI(true);
      setTemplates(response.data || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await fetchAllEmployeesAPI('', true, 1000);
      const employeesData = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];
      
      const filteredEmployees = employeesData.filter((emp: any) => {
        return emp.user_role === 'EMPLOYEE';
      });
      
      setEmployees(filteredEmployees);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    });
  };

  const handleWeightageChange = (field: string, value: number) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleParticipantToggle = (employeeId: number) => {
    setSelectedParticipantIds((prev) =>
      prev.includes(employeeId)
        ? prev.filter((id) => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleSelectAllParticipants = () => {
    if (selectedParticipantIds.length === employees.length) {
      setSelectedParticipantIds([]);
    } else {
      setSelectedParticipantIds(employees.map((emp) => emp.id));
    }
  };

  const calculateTotalWeightage = () => {
    let total = 0;
    if (formData.enable_self_review) total += formData.self_review_weightage;
    if (formData.enable_manager_review) total += formData.manager_review_weightage;
    if (formData.enable_peer_review) total += formData.peer_review_weightage;
    if (formData.enable_client_review) total += formData.client_review_weightage;
    return total;
  };

  const getRemainingWeightage = () => {
    return 100 - calculateTotalWeightage();
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      return "Cycle name is required";
    }
    if (!formData.form_opens) {
      return "Form opens date is required";
    }
    if (!formData.deadline) {
      return "Deadline is required";
    }
    if (new Date(formData.form_opens) >= new Date(formData.deadline)) {
      return "Form opens date must be before deadline";
    }

    const enabledCount = [
      formData.enable_self_review,
      formData.enable_manager_review,
      formData.enable_peer_review,
      formData.enable_client_review,
    ].filter(Boolean).length;

    if (enabledCount === 0) {
      return "At least one review type must be enabled";
    }

    if (formData.enable_self_review && !formData.self_review_template_id) {
      return "Self review template is required when self review is enabled";
    }
    if (formData.enable_manager_review && !formData.manager_review_template_id) {
      return "Manager review template is required when manager review is enabled";
    }
    if (formData.enable_peer_review && !formData.peer_review_template_id) {
      return "Peer review template is required when peer review is enabled";
    }
    if (formData.enable_client_review && !formData.client_review_template_id) {
      return "Client review template is required when client review is enabled";
    }

    const totalWeightage = calculateTotalWeightage();
    if (Math.abs(totalWeightage - 100) > 0.01) {
      return `Total weightage must be exactly 100%. Current: ${totalWeightage.toFixed(1)}%`;
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData: any = {
        name: formData.name,
        description: formData.description,
        form_opens: new Date(formData.form_opens).toISOString(),
        deadline: new Date(formData.deadline).toISOString(),
        is_active: formData.is_active,
        enable_self_review: formData.enable_self_review,
        enable_manager_review: formData.enable_manager_review,
        enable_peer_review: formData.enable_peer_review,
        enable_client_review: formData.enable_client_review,
        self_review_weightage: formData.self_review_weightage,
        manager_review_weightage: formData.manager_review_weightage,
        peer_review_weightage: formData.peer_review_weightage,
        client_review_weightage: formData.client_review_weightage,
        participant_ids: selectedParticipantIds,
      };

      if (formData.self_review_template_id) {
        submitData.self_review_template_id = parseInt(formData.self_review_template_id);
      } else {
        submitData.self_review_template_id = null;
      }
      if (formData.manager_review_template_id) {
        submitData.manager_review_template_id = parseInt(formData.manager_review_template_id);
      } else {
        submitData.manager_review_template_id = null;
      }
      if (formData.peer_review_template_id) {
        submitData.peer_review_template_id = parseInt(formData.peer_review_template_id);
      } else {
        submitData.peer_review_template_id = null;
      }
      if (formData.client_review_template_id) {
        submitData.client_review_template_id = parseInt(formData.client_review_template_id);
      } else {
        submitData.client_review_template_id = null;
      }

      await updateAppraisalCycleAPI(Number(cycleId!), submitData);
      toast.success("Appraisal cycle updated successfully!");
      navigate("/admin-appraisals?tab=1");
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to update appraisal cycle";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalWeightage = calculateTotalWeightage();
  const remainingWeightage = getRemainingWeightage();
  const templateOptions = [
    { value: "", label: "-- Select Template --" },
    ...templates.map((t) => ({ value: t.id.toString(), label: t.name })),
  ];

  if (isLoading) {
    return (
      <div className="mt-5 flex items-center justify-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Loading cycle...</p>
      </div>
    );
  }

  return (
    <div className="mt-5">
      <Card extra="p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
            Edit Appraisal Cycle
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Update the appraisal cycle configuration below.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white border-b pb-2">
              Basic Information
            </h3>

            <InputField
              id="name"
              name="name"
              label="Cycle Name *"
              type="text"
              placeholder="Enter cycle name"
              extra="w-full"
              variant="outlined"
              value={formData.name}
              onChange={handleChange}
              required
            />

            <TextField
              id="description"
              label="Description"
              placeholder="Enter cycle description"
              extra="w-full"
              rows={4}
              value={formData.description}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  description: e.target.value,
                });
              }}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                id="form_opens"
                name="form_opens"
                label="Form Opens *"
                type="datetime-local"
                placeholder=""
                extra="w-full"
                variant="outlined"
                value={formData.form_opens}
                onChange={handleChange}
                required
              />

              <InputField
                id="deadline"
                name="deadline"
                label="Deadline *"
                type="datetime-local"
                placeholder=""
                extra="w-full"
                variant="outlined"
                value={formData.deadline}
                onChange={handleChange}
                required
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="h-4 w-4 text-brand-500 focus:ring-brand-500 border-gray-300 rounded"
              />
              <label
                htmlFor="is_active"
                className="ml-2 text-sm font-medium text-navy-700 dark:text-white"
              >
                Active
              </label>
            </div>
          </div>

          <div className="space-y-4 pt-6 border-t">
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white border-b pb-2">
              Review Types & Templates
            </h3>

            <div className="space-y-6">
              <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-navy-700/30">
                <div className={`flex items-center justify-between ${formData.enable_self_review ? 'mb-4' : ''}`}>
                  <div className="flex items-center">
                    <Checkbox
                      id="enable_self_review"
                      name="enable_self_review"
                      checked={formData.enable_self_review}
                      onChange={handleChange}
                      extra="mr-2"
                    />
                    <label htmlFor="enable_self_review" className="text-sm font-semibold text-navy-700 dark:text-white">
                      Enable Self Review
                    </label>
                  </div>
                </div>
                {formData.enable_self_review && (
                  <div className="space-y-4 mt-4">
                    <SelectField
                      id="self_review_template_id"
                      name="self_review_template_id"
                      label="Self Review Template *"
                      options={templateOptions}
                      value={formData.self_review_template_id}
                      onChange={handleChange}
                      extra="w-full"
                    />
                    <Slider
                      id="self_review_weightage"
                      label="Self Review Weightage (%) *"
                      value={formData.self_review_weightage}
                      min={0}
                      max={100}
                      step={5}
                      onChange={(value) => handleWeightageChange("self_review_weightage", value)}
                      extra="w-full"
                      showError={formData.self_review_weightage > remainingWeightage + formData.self_review_weightage}
                    />
                  </div>
                )}
              </div>

              <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-navy-700/30">
                <div className={`flex items-center justify-between ${formData.enable_manager_review ? 'mb-4' : ''}`}>
                  <div className="flex items-center">
                    <Checkbox
                      id="enable_manager_review"
                      name="enable_manager_review"
                      checked={formData.enable_manager_review}
                      onChange={handleChange}
                      extra="mr-2"
                    />
                    <label htmlFor="enable_manager_review" className="text-sm font-semibold text-navy-700 dark:text-white">
                      Enable Manager Review
                    </label>
                  </div>
                </div>
                {formData.enable_manager_review && (
                  <div className="space-y-4 mt-4">
                    <SelectField
                      id="manager_review_template_id"
                      name="manager_review_template_id"
                      label="Manager Review Template *"
                      options={templateOptions}
                      value={formData.manager_review_template_id}
                      onChange={handleChange}
                      extra="w-full"
                    />
                    <Slider
                      id="manager_review_weightage"
                      label="Manager Review Weightage (%) *"
                      value={formData.manager_review_weightage}
                      min={0}
                      max={100}
                      step={5}
                      onChange={(value) => handleWeightageChange("manager_review_weightage", value)}
                      extra="w-full"
                      showError={formData.manager_review_weightage > remainingWeightage + formData.manager_review_weightage}
                    />
                  </div>
                )}
              </div>

              <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-navy-700/30">
                <div className={`flex items-center justify-between ${formData.enable_peer_review ? 'mb-4' : ''}`}>
                  <div className="flex items-center">
                    <Checkbox
                      id="enable_peer_review"
                      name="enable_peer_review"
                      checked={formData.enable_peer_review}
                      onChange={handleChange}
                      extra="mr-2"
                    />
                    <label htmlFor="enable_peer_review" className="text-sm font-semibold text-navy-700 dark:text-white">
                      Enable Peer Review
                    </label>
                  </div>
                </div>
                {formData.enable_peer_review && (
                  <div className="space-y-4 mt-4">
                    <SelectField
                      id="peer_review_template_id"
                      name="peer_review_template_id"
                      label="Peer Review Template *"
                      options={templateOptions}
                      value={formData.peer_review_template_id}
                      onChange={handleChange}
                      extra="w-full"
                    />
                    <Slider
                      id="peer_review_weightage"
                      label="Peer Review Weightage (%) *"
                      value={formData.peer_review_weightage}
                      min={0}
                      max={100}
                      step={5}
                      onChange={(value) => handleWeightageChange("peer_review_weightage", value)}
                      extra="w-full"
                      showError={formData.peer_review_weightage > remainingWeightage + formData.peer_review_weightage}
                    />
                  </div>
                )}
              </div>

              <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-navy-700/30">
                <div className={`flex items-center justify-between ${formData.enable_client_review ? 'mb-4' : ''}`}>
                  <div className="flex items-center">
                    <Checkbox
                      id="enable_client_review"
                      name="enable_client_review"
                      checked={formData.enable_client_review}
                      onChange={handleChange}
                      extra="mr-2"
                    />
                    <label htmlFor="enable_client_review" className="text-sm font-semibold text-navy-700 dark:text-white">
                      Enable Client Review
                    </label>
                  </div>
                </div>
                {formData.enable_client_review && (
                  <div className="space-y-4 mt-4">
                    <SelectField
                      id="client_review_template_id"
                      name="client_review_template_id"
                      label="Client Review Template *"
                      options={templateOptions}
                      value={formData.client_review_template_id}
                      onChange={handleChange}
                      extra="w-full"
                    />
                    <Slider
                      id="client_review_weightage"
                      label="Client Review Weightage (%) *"
                      value={formData.client_review_weightage}
                      min={0}
                      max={100}
                      step={5}
                      onChange={(value) => handleWeightageChange("client_review_weightage", value)}
                      extra="w-full"
                      showError={formData.client_review_weightage > remainingWeightage + formData.client_review_weightage}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm">
                <span className="text-gray-600 dark:text-gray-400">Total Weightage: </span>
                <span
                  className={`font-semibold ${
                    totalWeightage === 100
                      ? "text-green-600 dark:text-green-400"
                      : totalWeightage > 100
                      ? "text-red-600 dark:text-red-400"
                      : "text-blue-600 dark:text-blue-400"
                  }`}
                >
                  {totalWeightage.toFixed(1)}%
                </span>
                <span className="text-gray-600 dark:text-gray-400 ml-2">
                  Remaining: {remainingWeightage.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-4 pt-6 border-t">
            <div className="flex justify-between items-center">
              <label className="text-sm font-bold text-gray-600 dark:text-white">
                Participants (Employees)
              </label>
              <SelectAllButton
                isAllSelected={selectedParticipantIds.length === employees.length}
                onClick={handleSelectAllParticipants}
              />
            </div>
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 max-h-60 overflow-y-auto bg-white dark:bg-navy-800">
              {employees.length > 0 ? (
                employees.map((employee) => (
                  <div key={employee.id} className="flex items-center mb-2">
                    <Checkbox
                      checked={selectedParticipantIds.includes(employee.id)}
                      onChange={() => handleParticipantToggle(employee.id)}
                      extra="mr-2"
                    />
                    <label className="text-sm font-medium text-navy-700 dark:text-white">
                      {employee.full_name || `${employee.first_name} ${employee.last_name}`}
                    </label>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No employees available</p>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Selected: {selectedParticipantIds.length} employee(s). Managers and clients will be automatically assigned based on employee relationships.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              varient="secondary"
              onClick={() => navigate("/admin-appraisals?tab=1")}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" varient="primary" disabled={isSubmitting}>
              {isSubmitting ? "Updating..." : "Update Cycle"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default EditCycle;
